import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Icon } from '@iconify/react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  InputAdornment,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useForm } from 'react-hook-form';
import { FormProvider, RHFSelect, RHFTextField } from '../../components/hook-form';
import { fNumber } from '../../utils/formatNumber';
import CardRedemptionDialogFrame from './CardRedemptionDialogFrame';



const DELAY_OPTIONS = [
  { value: '', label: '-- Select payment delay --', prefill: '' },
  {
    value: 2,
    label: 'After 2 Minutes',
  },
  {
    value: 5,
    label: 'After 5 Minutes',
  },
  {
    value: 10,
    label: 'After 10 Minutes',
  },
  {
    value: 20,
    label: 'After 20 Minutes',
  },
  {
    value: 30,
    label: 'After 30 Minutes',
  },
  {
    value: 45,
    label: 'After 45 Minutes',
  },
  {
    value: 60,
    label: 'After 1 hour',
  },
  {
    value: 90,
    label: 'After 1 hour - 30 Minutes',
  },
  {
    value: 120,
    label: 'After 2 hours',
  },
];


const completeSchema = Yup.object().shape({
  total_amount: Yup.string().required('Total amount is required'),
  settlement_type: Yup.string().required('Please select a settlement type'),
});

function formatCurrencyField(value) {
  const normalizedValue = String(value || '').replaceAll(',', '').trim();

  if (!normalizedValue) {
    return '';
  }

  const numericValue = Number(normalizedValue);

  if (Number.isNaN(numericValue)) {
    return value;
  }

  return numericValue.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
}

export default function CompleteOrderDialog({ loading, onClose, onSubmit, open, order }) {
  const [files, setFiles] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const methods = useForm({
    resolver: yupResolver(completeSchema),
    defaultValues: {
      total_amount: '',
      settlement_type: 'offline_payment',
      silent_notification: false,
      delay_minutes: 2
    },
  });

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isSubmitting },
  } = methods;

  const values = watch();

  useEffect(() => {
    reset({
      total_amount: '',
      settlement_type: 'offline_payment',
      silent_notification: false,
      delay_minutes: 2,
    });
    setFiles([]);
  }, [open, order, reset]);

  const handleFileChange = (event) => {
    setFiles(Array.from(event.target.files || []));
  };

  return (
    <CardRedemptionDialogFrame
      open={open}
      onClose={onClose}
      title="Complete Card Redemption Order"
      actions={
        <LoadingButton
          variant="contained"
          loading={loading || isSubmitting}
          onClick={handleSubmit((formValues) => onSubmit({ ...formValues, files }))}
        >
          Complete Order
        </LoadingButton>
      }
    >
      <FormProvider methods={methods} onSubmit={handleSubmit((formValues) => onSubmit({ ...formValues, files }))}>
        <Box>
          <Paper
            elevation={0}
            sx={{
              p: isMobile ? 1 : 3,
              bgcolor: 'background.default',
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Upload Proof of Transaction
            </Typography>
            <Stack
              direction={isDesktop ? 'row' : 'column'}
              alignItems={isDesktop ? 'center' : 'stretch'}
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Button
                component="label"
                variant="outlined"
                startIcon={<Icon icon="mdi:upload" width={20} height={20} />}
                sx={{ textTransform: 'none', fontWeight: 500, minWidth: 180 }}
              >
                Select Images
                <input hidden multiple type="file" accept="image/*" onChange={handleFileChange} />
              </Button>

              {files.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                  {files.map((file) => (
                    <Box
                      key={`${file.name}-${file.lastModified}`}
                      sx={{
                        bgcolor: 'grey.100',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: 12,
                        display: 'flex',
                        alignItems: 'center',
                        mb: 0.5,
                      }}
                    >
                      <Icon
                        icon="mdi:file-upload-outline"
                        width={16}
                        height={16}
                        style={{ marginRight: 4 }}
                      />
                      {file.name}
                    </Box>
                  ))}
                </Stack>
              )}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Settlement Review
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Enter the total amount received after trading the card. 
            </Typography>

            <RHFTextField
              name="total_amount"
              label={`Total Received Amount (${order?.settlement_currency || 'NGN'})`}
              InputProps={{
                inputMode: 'decimal',
                endAdornment: (
                  <InputAdornment position="end">{order?.settlement_currency || 'NGN'}</InputAdornment>
                ),
              }}
              required
              sx={{ mb: 2 }}
              onFocus={(event) => event.currentTarget.select()}
              onBlur={(event) => {
                setValue('total_amount', formatCurrencyField(event.currentTarget.value), {
                  shouldValidate: true,
                });
              }}
            />

            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              When to sent to out
            </Typography>
            <RHFSelect name="delay_minutes" sx={{ mt: 1, width: '100%' }}>
              {DELAY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </RHFSelect>

            {/* <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Settlement Type
            </Typography>
            <RHFSelect
              name="settlement_type"
              fullWidth
              required
              sx={{
                mb: 2,
                minHeight: isMobile ? 48 : 56,
                fontSize: isMobile ? 16 : 18,
              }}
            >
              <option value="" disabled>
                -- Select Settlement Type --
              </option>
              <option value="offline_payment">Manual Payment (No transfer)</option>
              <option value="paystack_transfer">Paystack transfer</option>
            </RHFSelect> */}

            <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 1 }}>
             Review changes: Payment will the initiated after this action
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={values.silent_notification}
                  onChange={(event) =>
                    setValue('silent_notification', event.target.checked, { shouldDirty: true })
                  }
                  color="error"
                />
              }
              label="I agree and take responsibility for this action."
            />
          </Paper>
        </Box>
      </FormProvider>
    </CardRedemptionDialogFrame>
  );
}

CompleteOrderDialog.propTypes = {
  loading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  order: PropTypes.object,
};
